import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Stethoscope, Scissors, GraduationCap, Utensils, Dumbbell, Shirt,
  ShoppingBasket, Wrench, ArrowRight, ArrowLeft, Sparkles, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";

const ICONS = {
  stethoscope: Stethoscope, scissors: Scissors, "graduation-cap": GraduationCap,
  utensils: Utensils, dumbbell: Dumbbell, shirt: Shirt,
  "shopping-basket": ShoppingBasket, wrench: Wrench,
};

const inputCls = "w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C9A24B] focus:ring-1 focus:ring-[#C9A24B] rounded-xl px-4 py-3 outline-none text-[#F5F5F7] placeholder:text-zinc-600 text-sm transition-all";

export default function Create() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [themes, setThemes] = useState([]);
  const [form, setForm] = useState({
    business_name: "", city: "", category: "", description: "",
    phone: "", whatsapp: "", address: "", theme_id: "",
  });

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCats(data.categories);
      setThemes(data.themes);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = ["Business", "Category", "About & Contact", "Style"];

  const canNext = () => {
    if (step === 0) return form.business_name.trim().length > 1;
    if (step === 1) return !!form.category;
    if (step === 2) return form.description.trim().length > 5 && form.phone.replace(/\D/g, "").length >= 10;
    return true;
  };

  const submit = () => {
    if (!form.theme_id && form.category) {
      const c = cats.find((x) => x.id === form.category);
      form.theme_id = c?.default_theme || "";
    }
    navigate("/generating", { state: { form } });
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-gradient-to-r from-[#C9A24B] to-[#E5C378]" : "bg-[#27272A]"}`} />
              <span className={`text-[11px] mt-2 block ${i <= step ? "text-[#E5C378]" : "text-zinc-600"}`}>{s}</span>
            </div>
          ))}
        </div>

        <div className="bg-[#161616] border border-[#27272A] rounded-3xl p-6 sm:p-8 wt-fade" key={step}>
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-display text-2xl font-bold">Tell us about your business</h2>
                <p className="text-zinc-400 text-sm mt-1">This is how customers will find you.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Business Name</label>
                <input data-testid="intake-business-name-input" className={`mt-2 ${inputCls}`}
                  value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                  placeholder="e.g. Sharma Dental Care" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">City / Area</label>
                <input data-testid="intake-city-input" className={`mt-2 ${inputCls}`}
                  value={form.city} onChange={(e) => set("city", e.target.value)}
                  placeholder="e.g. Indore" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl font-bold">What kind of business?</h2>
              <p className="text-zinc-400 text-sm mt-1 mb-5">We pick the best templates & photos for your type.</p>
              <div className="grid grid-cols-2 gap-3" data-testid="intake-category-select">
                {cats.map((c) => {
                  const Icon = ICONS[c.icon] || Sparkles;
                  const sel = form.category === c.id;
                  return (
                    <button key={c.id} data-testid={`intake-category-${c.id}`}
                      onClick={() => set("category", c.id)}
                      className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-all ${sel ? "border-[#C9A24B] bg-[#C9A24B]/10" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#C9A24B]/40"}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${sel ? "bg-[#C9A24B] text-[#0A0A0A]" : "bg-[#1C1C1E] text-[#C9A24B]"}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-medium">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-display text-2xl font-bold">Details for your site</h2>
                <p className="text-zinc-400 text-sm mt-1">A few lines — our AI turns it into full website copy.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Describe your business</label>
                <textarea data-testid="intake-description-input" rows={4} className={`mt-2 ${inputCls} resize-none`}
                  value={form.description} onChange={(e) => set("description", e.target.value)}
                  placeholder="What do you offer? Any specialities, timings, experience? Hinglish is fine." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Phone</label>
                  <input data-testid="intake-phone-input" inputMode="numeric" className={`mt-2 ${inputCls}`}
                    value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="98765 43210" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">WhatsApp (optional)</label>
                  <input data-testid="intake-whatsapp-input" inputMode="numeric" className={`mt-2 ${inputCls}`}
                    value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Same as phone" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Address (optional)</label>
                <input data-testid="intake-address-input" className={`mt-2 ${inputCls}`}
                  value={form.address} onChange={(e) => set("address", e.target.value)}
                  placeholder="Shop no, street, landmark" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl font-bold">Pick a style</h2>
              <p className="text-zinc-400 text-sm mt-1 mb-5">Our AI may fine-tune this to fit your business.</p>
              <div className="grid grid-cols-2 gap-3">
                {themes.map((t) => {
                  const sel = form.theme_id === t.id;
                  return (
                    <button key={t.id} data-testid={`intake-theme-option-${t.id}`}
                      onClick={() => set("theme_id", t.id)}
                      className={`relative p-4 rounded-2xl border text-left transition-all ${sel ? "border-[#C9A24B]" : "border-[#27272A] hover:border-[#C9A24B]/40"}`}
                      style={{ background: t.bg }}>
                      {sel && <Check className="absolute top-3 right-3 w-5 h-5 text-[#C9A24B]" />}
                      <div className="flex gap-1.5 mb-3">
                        <span className="w-6 h-6 rounded-full" style={{ background: t.accent }} />
                        <span className="w-6 h-6 rounded-full" style={{ background: t.surface, border: "1px solid " + t.border }} />
                      </div>
                      <span className="font-display font-bold text-sm" style={{ color: t.text }}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => (step === 0 ? navigate("/dashboard") : setStep(step - 1))}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-zinc-300 hover:text-white bg-[#1C1C1E] border border-[#27272A] transition-all text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button data-testid="intake-next-btn"
              onClick={() => canNext() ? setStep(step + 1) : toast.error("Please fill the required fields")}
              className={`inline-flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] ${canNext() ? "bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A]" : "bg-[#27272A] text-zinc-500"}`}>
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="intake-generate-btn"
              onClick={submit}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] transition-all active:scale-[0.98] wt-pulse">
              <Sparkles className="w-4 h-4" /> Generate My Website
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
